import classNames from "classnames";
import { memo } from "react";

import { useGetFormVisibility } from "../../hooks";
import { endBracket, startBracket } from "../../utilities/validationRegex";
import { ComponentsFactory } from "../ComponentsFactory";
import { FormHeading } from "../FormHeading";
import { Grid, GridProps } from "../Grid";
import { FormId } from "../enums";
import {
  ComplexComponentsArray,
  ComplexFormSchemaComponents,
  FormSchema,
  IsVisible,
  SimpleFormSchemaComponents,
} from "../types";

import styles from "./Section.module.scss";

const generateComponentId = (formId: FormId | undefined, id: string) =>
  formId ? `${formId}[${id}]` : id;

const getNestedDataByName = (formData: any, componentId: string) =>
  componentId
    .replace(startBracket, ".")
    .replace(endBracket, "")
    .split(".")
    .reduce((current, key) => current?.[key], formData);

export type SectionProps<C> = {
  readonly components: Array<C>;
  readonly id: string;
  readonly wrapper: GridProps;
  readonly title?: string;
  readonly isVisible?: IsVisible;
};

export const Section = memo<
  SectionProps<ComplexFormSchemaComponents | SimpleFormSchemaComponents> &
    Pick<
      FormSchema<ComplexFormSchemaComponents | SimpleFormSchemaComponents>,
      "formId"
    > & {
      readonly formData: any;
    }
>(
  ({
    components,
    formData,
    formId,
    id: sectionId,
    isVisible: isVisibleSelector,
    title,
    wrapper,
  }) => {
    const isVisible = useGetFormVisibility(isVisibleSelector, sectionId);
    const isFullWidth = components.length === 1;
    const rowClasses = classNames(styles.row, {
      [styles.row_full]: isFullWidth,
    });

    if (!isVisible) {
      return null;
    }

    return (
      <>
        <FormHeading
          key={["subtitle", title].join("-")}
          component="h3"
          title={title}
          variant="body1"
        />
        <Grid item key={sectionId} className={rowClasses} {...wrapper}>
          {components.map((component) => {
            let defaultValueFromComponent;

            if ("defaultValue" in component) {
              defaultValueFromComponent = component.defaultValue;
            }

            const newComponent = Object.assign(
              {},
              { ...component },
              {
                isComplex: ComplexComponentsArray.includes(component.type),
                defaultValue:
                  formData?.[component.id] ||
                  getNestedDataByName(formData, component.id) ||
                  defaultValueFromComponent ||
                  "",
                id: generateComponentId(formId, component.id),
              }
            );

            return (
              <ComponentsFactory
                key={[sectionId, component.id].join("-")}
                component={newComponent}
              />
            );
          })}
        </Grid>
      </>
    );
  }
);
